import React, { useCallback, useMemo, useState } from 'react';

import { Box, Chip, Container, TextField, Tooltip, Typography } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { generatePath, useHistory, useParams } from 'react-router-dom';

import ColoredButton from 'components/ColoredButton';
import ErrorBoundary from 'components/ErrorBoundary';
import Loader from 'components/Loader';
import ManagePageHeader from 'components/ManagePageHeader/ManagePageHeader';
import TextButton from 'components/TextButton/TextButton';
import MUTATION_KEYS from 'config/api/MUTATION_KEYS';
import QUERY_KEYS from 'config/api/QUERY_KEYS';
import ropaReviewsApi from 'config/api/ropaReviews/ropaReviews';
import usePageTitle from 'hooks/usePageTitle';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import userDataStorage from 'storages/userDataStorage';

import {
  AssetProcessingCountryRowsSection,
  AssetProcessingDpiaSection,
  AssetProcessingPurposeApplicationsSection,
  AssetProcessingSecuritySection,
} from '../_shared/AssetProcessingApaSections/AssetProcessingApaSections';
import { canAccessAdminApaReviewPage } from '../_shared/assetRopaReviewAccess';
import { isReviewLocked } from '../_shared/assetRopaReviewHelpers';
import AssetRopaWarningChips from '../_shared/AssetRopaWarningChips/AssetRopaWarningChips';
import { getDpiaFeatureDisabledTooltip, IS_DPIA_TEMPORARILY_DISABLED } from '../_shared/dpiaFeatureFlags';
import ProcessingActivitySidePanel from '../_shared/ProcessingActivitySidePanel/ProcessingActivitySidePanel';
import {
  mapDetailToFormValues,
  mapProcessingActivityToSidePanel,
} from '../AssetProcessingActivityEditorPage/assetProcessingActivityEditor.helpers';
import RequestClarificationModal from '../ProcessingActivitiesAssetReviewsPage/_components/RequestClarificationModal';
import { DEFAULT_ASSET_ROPA_REVIEW_FILTERS } from '../ProcessingActivitiesAssetReviewsPage/assetRopaReview.types';
import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';
import useAssetRopaReviewPage from '../ProcessingActivitiesAssetReviewsPage/useAssetRopaReviewPage';

import { getAcceptDisabledTooltip, getClarificationDisabledTooltip } from './assetProcessingActivityReview.helpers';

type RouteParams = {
  reviewRowId: string;
};

const AssetProcessingActivityReviewPage: React.FC = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { reviewRowId } = useParams<RouteParams>();
  const { enqueueSnackbar } = useSnackbar();
  const { showConfirmationModal } = useConfirmationModalContext();
  const userData = userDataStorage.get() || {};
  const organizationId = typeof userData.organization === 'number' ? userData.organization : undefined;
  const canAccess = canAccessAdminApaReviewPage(userData);
  const [clarificationOpen, setClarificationOpen] = useState(false);
  const [acceptComment, setAcceptComment] = useState('');

  const { activeReview, reviewRoundId } = useAssetRopaReviewPage({
    filters: DEFAULT_ASSET_ROPA_REVIEW_FILTERS,
    organizationId,
    variant: 'admin',
  });

  const detailQuery = useQuery(
    [QUERY_KEYS.ROPA_REVIEWS_PROCESSING_REVIEW_DETAIL, organizationId, reviewRowId],
    async () => {
      const { data } = await ropaReviewsApi.getAssetProcessingActivityReviewDetail(organizationId as number, Number(reviewRowId));
      return data;
    },
    {
      enabled: Boolean(organizationId && reviewRowId),
      retry: false,
    },
  );

  const acceptMutation = useMutation(MUTATION_KEYS.ROPA_REVIEWS_ACCEPT_PROCESSING_REVIEW, async (comment?: string) => {
    await ropaReviewsApi.acceptAssetProcessingActivityReview(organizationId as number, Number(reviewRowId), {
      comment: comment?.trim() || undefined,
    });
  });

  const clarificationMutation = useMutation(MUTATION_KEYS.ROPA_REVIEWS_REQUEST_CLARIFICATION, async (input: { comment: string }) => {
    await ropaReviewsApi.requestAssetProcessingActivityReviewClarification(organizationId as number, Number(reviewRowId), {
      comment: input.comment,
    });
  });

  const navigateBack = useCallback(() => {
    history.push(PATHS.PROCESSING_ACTIVITIES_ASSET_REVIEWS);
  }, [history]);

  const reviewLocked = activeReview ? isReviewLocked(activeReview.review.status) : false;
  const detail = detailQuery.data;
  const messages = processing_activities_messages.asset_ropa.apa_review;

  usePageTitle(detail?.processingActivity?.name ?? t(messages.page_title));

  const sidePanelActivity = useMemo(() => {
    if (!detail?.processingActivity) return null;

    try {
      return mapProcessingActivityToSidePanel(detail);
    } catch {
      return null;
    }
  }, [detail]);

  const formValues = useMemo(() => {
    if (!detail?.processingActivity) return null;

    try {
      return mapDetailToFormValues(detail);
    } catch {
      return null;
    }
  }, [detail]);

  const handleAccept = useCallback(async () => {
    if (!detail || detail.changedAfterSubmission) return;

    const confirmed = await showConfirmationModal({
      title: t(processing_activities_messages.asset_ropa.actions.accept),
      body: detail.processingActivity?.name ?? '',
    });

    if (!confirmed) return;

    try {
      await acceptMutation.mutateAsync(acceptComment);
      enqueueSnackbar(t(messages.toasts.accepted), { variant: 'success' });
      navigateBack();
    } catch {
      enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
    }
  }, [acceptComment, acceptMutation, detail, enqueueSnackbar, messages, navigateBack, showConfirmationModal, t]);

  const handleClarificationConfirm = useCallback(
    async (comment: string) => {
      try {
        await clarificationMutation.mutateAsync({ comment });
        enqueueSnackbar(t(messages.toasts.clarification_requested), { variant: 'success' });
        setClarificationOpen(false);
        navigateBack();
      } catch {
        enqueueSnackbar(t(processing_activities_messages.asset_ropa.toasts.action_failed), { variant: 'error' });
      }
    },
    [clarificationMutation, enqueueSnackbar, messages, navigateBack, t],
  );

  const handleAssessDpia = useCallback(() => {
    if (!detail) return;

    history.push({
      pathname: generatePath(PATHS.PROCESSING_ACTIVITIES_DPIA_ASSESSMENT, {
        apaId: String(detail.id),
      }),
      state: {
        returnPath: history.location.pathname,
        sourceAssetProcessingActivityReviewId: Number(reviewRowId),
        sourceRopaReviewRoundId: reviewRoundId ?? undefined,
      },
    });
  }, [detail, history, reviewRoundId, reviewRowId]);

  const handleOpenAdminDpiaReview = useCallback(() => {
    if (!detail?.dpiaId) return;

    history.push(
      generatePath(PATHS.PROCESSING_ACTIVITIES_ADMIN_DPIA_REVIEW, {
        dpiaId: String(detail.dpiaId),
      }),
      { returnPath: history.location.pathname },
    );
  }, [detail?.dpiaId, history]);

  const pageTitle = detail?.processingActivity?.name || t(messages.page_title);

  let content: React.ReactNode;

  if (!canAccess) {
    content = (
      <Typography color='textSecondary' variant='body2'>
        {t(processing_activities_messages.asset_ropa.forbidden_body)}
      </Typography>
    );
  } else if (detailQuery.isLoading) {
    content = <Loader inner />;
  } else if (detailQuery.isError || !detail || !formValues || !sidePanelActivity) {
    content = (
      <Box className={styles.errorBanner}>
        <Typography color='error' variant='body2'>
          {t(processing_activities_messages.asset_ropa.errors.load_failed)}
        </Typography>
        <TextButton onClick={() => detailQuery.refetch()}>{t(general_messages.retry)}</TextButton>
      </Box>
    );
  } else {
    const submittedRevision = detail.reviewRow?.submittedRevision ?? detail.review?.submittedRevision ?? null;
    const submittedAt = detail.reviewRow?.submittedAt ?? detail.review?.submittedAt ?? null;
    const submittedBy =
      detail.reviewRow?.submittedByName ??
      (detail.reviewRow?.submittedByMembershipId
        ? t(processing_activities_messages.asset_ropa.active_review.membership_label, {
            id: detail.reviewRow.submittedByMembershipId,
          })
        : null);
    const clarificationComment = detail.review?.clarificationComment || detail.reviewRow?.clarificationComment;
    const disabledTooltipMessages = {
      acceptBlockedChangedAfterSubmission: t(messages.accept_blocked),
      acceptAwaitingResponse: t(messages.disabled_tooltips.accept_awaiting_response),
      acceptClarificationRequested: t(messages.disabled_tooltips.accept_clarification_requested),
      acceptAlreadyAccepted: t(messages.disabled_tooltips.accept_already_accepted),
      acceptUnavailable: t(messages.disabled_tooltips.accept_unavailable),
      clarificationAwaitingResponse: t(messages.disabled_tooltips.clarification_awaiting_response),
      clarificationAlreadyRequested: t(messages.disabled_tooltips.clarification_already_requested),
      clarificationAlreadyAccepted: t(messages.disabled_tooltips.clarification_already_accepted),
      clarificationUnavailable: t(messages.disabled_tooltips.clarification_unavailable),
    };
    const acceptDisabled = !detail.actions?.canAccept || detail.changedAfterSubmission || acceptMutation.isLoading;
    const clarificationDisabled = !detail.actions?.canRequestClarification || clarificationMutation.isLoading;
    const acceptDisabledTooltip = acceptDisabled ? getAcceptDisabledTooltip(detail, disabledTooltipMessages) : '';
    const clarificationDisabledTooltip = clarificationDisabled ? getClarificationDisabledTooltip(detail, disabledTooltipMessages) : '';
    const dpiaFeatureDisabledTooltip = IS_DPIA_TEMPORARILY_DISABLED ? getDpiaFeatureDisabledTooltip(t) : '';

    content = (
      <>
        {(detail.handlesSensitivePersonalData || detail.dpiaIndicationStatus === 'indicated') && (
          <AssetRopaWarningChips
            warnings={{
              dpiaIndicated: detail.dpiaIndicationStatus === 'indicated',
              dpiaRequiredUnapproved: false,
              thirdCountryProcessing: detail.handlesProcessingOutsideEuEea,
              sensitivePersonalData: detail.handlesSensitivePersonalData,
              confirmedNoProcessingActivities: false,
            }}
          />
        )}

        <Box className={styles.reviewPanel} style={{ marginBottom: 16 }}>
          <div className={styles.reviewMeta}>
            <Typography variant='body2'>{t(messages.current_revision, { revision: detail.revision })}</Typography>
            {submittedRevision !== null && (
              <Typography variant='body2'>{t(messages.submitted_revision, { revision: submittedRevision })}</Typography>
            )}
            {submittedAt && (
              <Typography variant='body2'>{t(messages.submitted_at, { date: submittedAt.slice(0, 16).replace('T', ' ') })}</Typography>
            )}
            {submittedBy && <Typography variant='body2'>{t(messages.submitted_by, { name: submittedBy })}</Typography>}
          </div>
        </Box>

        {detail.changedAfterSubmission && (
          <Box className={styles.reviewBanner}>
            <Chip color='secondary' label={t(messages.changed_after_submission)} />
            <Typography color='textSecondary' style={{ marginTop: 8 }} variant='body2'>
              {t(messages.accept_blocked)}
            </Typography>
          </Box>
        )}

        <Typography className={styles.pageHelp} color='textSecondary' variant='body2'>
          {t(messages.truth_note)}
        </Typography>

        <div className={styles.twoColumnLayout}>
          <ProcessingActivitySidePanel activity={sidePanelActivity} />
          <section className={styles.mainPanel}>
            <Typography gutterBottom variant='h3'>
              {t(messages.asset_section_title)}
            </Typography>
            <AssetProcessingSecuritySection onChange={() => undefined} readOnly values={formValues} />
            <AssetProcessingPurposeApplicationsSection
              centralPurposes={sidePanelActivity.purposes}
              onChange={() => undefined}
              readOnly
              values={formValues}
            />
            <AssetProcessingCountryRowsSection onChange={() => undefined} readOnly values={formValues} />
            <AssetProcessingDpiaSection onChange={() => undefined} readOnly values={formValues} />
            {clarificationComment && (
              <Box className={styles.reviewBanner}>
                <Typography variant='body2'>
                  <strong>{t(messages.clarification_comment)}:</strong> {clarificationComment}
                </Typography>
              </Box>
            )}
            {!reviewLocked && (
              <>
                <TextField
                  fullWidth
                  label={t(messages.accept_comment)}
                  margin='normal'
                  multiline
                  onChange={event => setAcceptComment(event.target.value)}
                  rows={3}
                  value={acceptComment}
                  variant='outlined'
                />
                <div className={styles.pageActions}>
                  <Tooltip title={dpiaFeatureDisabledTooltip}>
                    <Box className={styles.tooltipTrigger} component='span'>
                      <ColoredButton
                        className=''
                        customColor='grey'
                        disabled={IS_DPIA_TEMPORARILY_DISABLED}
                        icon={false}
                        onClick={handleAssessDpia}
                        showLoader={false}
                        textLike={false}
                        variant='outlined'
                      >
                        {t(messages.assess_dpia)}
                      </ColoredButton>
                    </Box>
                  </Tooltip>
                  {detail.dpiaId && (
                    <Tooltip title={dpiaFeatureDisabledTooltip}>
                      <Box className={styles.tooltipTrigger} component='span'>
                        <ColoredButton
                          className=''
                          customColor='grey'
                          disabled={IS_DPIA_TEMPORARILY_DISABLED}
                          icon={false}
                          onClick={handleOpenAdminDpiaReview}
                          showLoader={false}
                          textLike={false}
                          variant='outlined'
                        >
                          {t(messages.open_admin_dpia_review)}
                        </ColoredButton>
                      </Box>
                    </Tooltip>
                  )}
                  <Tooltip title={acceptDisabledTooltip}>
                    <Box className={styles.tooltipTrigger} component='span'>
                      <ColoredButton
                        className=''
                        customColor='secondary'
                        disabled={acceptDisabled}
                        icon={false}
                        onClick={handleAccept}
                        showLoader={acceptMutation.isLoading}
                        textLike={false}
                        variant='contained'
                      >
                        {t(processing_activities_messages.asset_ropa.actions.accept)}
                      </ColoredButton>
                    </Box>
                  </Tooltip>
                  <Tooltip title={clarificationDisabledTooltip}>
                    <Box className={styles.tooltipTrigger} component='span'>
                      <ColoredButton
                        className=''
                        customColor='grey'
                        disabled={clarificationDisabled}
                        icon={false}
                        onClick={() => setClarificationOpen(true)}
                        showLoader={false}
                        textLike={false}
                        variant='outlined'
                      >
                        {t(processing_activities_messages.asset_ropa.actions.request_clarification)}
                      </ColoredButton>
                    </Box>
                  </Tooltip>
                </div>
              </>
            )}
          </section>
        </div>
      </>
    );
  }

  return (
    <ErrorBoundary>
      <Container>
        <ManagePageHeader backButton noBorder onBack={navigateBack} subtitle={t(messages.page_subtitle)}>
          {pageTitle}
        </ManagePageHeader>
        {content}
        <RequestClarificationModal
          loading={clarificationMutation.isLoading}
          onClose={() => setClarificationOpen(false)}
          onConfirm={handleClarificationConfirm}
          open={clarificationOpen}
        />
      </Container>
    </ErrorBoundary>
  );
};

export default AssetProcessingActivityReviewPage;
